import { Box, styled } from '@mui/material';
import { ChartsLabel, ChartsLabelMark, labelMarkClasses } from '@mui/x-charts/ChartsLabel';
import { MarkShape } from '@mui/x-charts/models';
import React from 'react';

import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { useOptionalChartSettings } from 'contexts/chart/chart-settings-context';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import { ChartSurfaceProps } from 'components/charts/types/chart-surface.type';

type LegendItem = { id: string; label: string; color: string; shape?: MarkShape };

type Props = ChartSurfaceProps & {
  items: LegendItem[];
  onItemHover?: (id: string) => void;
  onItemLeave?: () => void;
} & (
    | { readOnly?: boolean; hiddenIds: string[]; onToggle: (id: string) => void }
    | { readOnly: true; hiddenIds?: string[]; onToggle?: (id: string) => void }
  );

const LegendRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'dense',
})<{ dense: boolean }>(({ theme, dense }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(dense ? 2 : 4),
}));

const LegendEntry = styled('button', {
  shouldForwardProp: (prop) => prop !== 'chartPalette' && prop !== 'readOnly' && prop !== 'as',
})<{ chartPalette: ChartPalette; readOnly?: boolean }>(({ theme, chartPalette, readOnly }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: readOnly ? 'default' : 'pointer',
  fontFamily: theme.typography.fontFamily,
  fontSize: 13,
  fontWeight: 500,
  color: chartPalette.text.primary,

  '&[aria-pressed="true"]': {
    color: chartPalette.text.disabled,
    textDecoration: 'line-through',
  },
}));

const MarkerWrapper = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isHidden',
})<{ isHidden: boolean }>(({ isHidden }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  filter: isHidden ? 'grayscale(1)' : 'none',
  opacity: isHidden ? 0.5 : 1,
  // Match the shared native legend swatch rather than the larger tooltip mark.
  [`& .${labelMarkClasses.root}.${labelMarkClasses.square}`]: { width: 10, height: 10, borderRadius: 3 },
}));

const ChartLegend: React.FC<Props> = (props) => {
  const { items, hiddenIds = [], onItemHover, onItemLeave } = props;
  const palette = useChartPalette(props.dark);
  const settings = useOptionalChartSettings()?.settings;

  return (
    <LegendRoot dense={settings?.density === 'compact'}>
      {items.map((item) => {
        const hidden = hiddenIds.includes(item.id);
        const mark = (
          <MarkerWrapper isHidden={hidden}>
            <ChartsLabelMark type={item.shape ? 'line+mark' : 'square'} markShape={item.shape} color={item.color} />
          </MarkerWrapper>
        );

        if (props.readOnly) {
          return (
            <LegendEntry as="div" key={item.id} chartPalette={palette} readOnly>
              {mark}
              <ChartsLabel>{item.label}</ChartsLabel>
            </LegendEntry>
          );
        }

        return (
          <LegendEntry
            key={item.id}
            type="button"
            chartPalette={palette}
            aria-pressed={hidden}
            onClick={() => props.onToggle(item.id)}
            onMouseEnter={() => !hidden && onItemHover?.(item.id)}
            onMouseLeave={() => onItemLeave?.()}
            onFocus={() => !hidden && onItemHover?.(item.id)}
            onBlur={() => onItemLeave?.()}
          >
            {mark}
            <ChartsLabel>{item.label}</ChartsLabel>
          </LegendEntry>
        );
      })}
    </LegendRoot>
  );
};

export default ChartLegend;
